// src/LoginPage.js
import * as React from "react";
import { useState } from "react";
import { useLogin, useNotify } from "react-admin";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const DEMO_SLUG  = 'demo';
const DEMO_EMAIL = 'demo@demo.demo';
const DEMO_PASS  = 'demo-pass';

const isDemoUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.has('demo') || window.location.pathname.toLowerCase().includes('/demo');
};

const timpPanaLaReset = () => {
    const acum = new Date();
    const reset = new Date();
    reset.setHours(3, 0, 0, 0);
    if (reset <= acum) reset.setDate(reset.getDate() + 1);
    const diff = reset - acum;
    const ore = Math.floor(diff / 3600000);
    const minute = Math.floor((diff % 3600000) / 60000);
    if (ore === 0) return `${minute} minute`;
    if (minute === 0) return `${ore}h`;
    return `${ore}h ${minute}min`;
};

export const LoginPage = () => {
    const [email,    setEmail]    = useState('');
    const [password, setPassword] = useState('');
    const [slug,     setSlug]     = useState('');
    const [loading,  setLoading]  = useState(false);

    const login  = useLogin();
    const notify = useNotify();

    const showDemo = isDemoUrl();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login({ username: email, password, slug });
        } catch (err) {
            notify(err.message || 'Email, parola sau organizatie incorecta', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDemoLogin = async () => {
        setLoading(true);
        try {
            await login({ username: DEMO_EMAIL, password: DEMO_PASS, slug: DEMO_SLUG });
        } catch (err) {
            notify(err.message || 'Eroare autentificare demo', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
        }}>
            <Box sx={{ width: '100%', maxWidth: 400 }}>
                {/* Logo / titlu */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <AccessTimeIcon sx={{ fontSize: 56, color: 'white', mb: 1 }} />
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                        Time Track
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
                        Gestionare ore echipe
                    </Typography>
                </Box>

                {/* Card demo - vizibil doar pe URL cu ?demo sau /demo */}
                {showDemo && (
                    <Card elevation={4} sx={{
                        borderRadius: 2,
                        mb: 2,
                        border: '1px solid rgba(255,193,7,0.6)',
                        background: 'rgba(255,193,7,0.08)',
                        backdropFilter: 'blur(4px)',
                    }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
                                🚀 Cont demo — testează gratuit
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', width: 80 }}>Organizatie</Typography>
                                    <Chip label={DEMO_SLUG} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontFamily: 'monospace' }} />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', width: 80 }}>Email</Typography>
                                    <Chip label={DEMO_EMAIL} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontFamily: 'monospace' }} />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', width: 80 }}>Parola</Typography>
                                    <Chip label={DEMO_PASS} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontFamily: 'monospace' }} />
                                </Box>
                            </Box>
                            <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                startIcon={<PlayArrowIcon />}
                                onClick={handleDemoLogin}
                                disabled={loading}
                                sx={{
                                    bgcolor: '#f9a825',
                                    color: '#000',
                                    fontWeight: 700,
                                    '&:hover': { bgcolor: '#f57f17' },
                                }}
                            >
                                Intră în demo
                            </Button>
                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'rgba(255,255,255,0.6)' }}>
                                🔄 Datele se resetează în ~{timpPanaLaReset()}
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                {/* Card login normal */}
                <Card elevation={8} sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                        <form onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                label="Organizatie (slug)"
                                value={slug}
                                onChange={(e) => setSlug(e.target.value)}
                                placeholder="ex: aquis-grana"
                                autoCapitalize="none"
                                autoCorrect="off"
                                sx={{ mb: 2 }}
                                helperText="Lasa gol daca esti superadmin"
                            />
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Parola"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                                sx={{ mb: 3 }}
                            />
                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loading}
                                sx={{ py: 1.5 }}
                            >
                                {loading
                                    ? <CircularProgress size={24} color="inherit" />
                                    : 'Autentificare'
                                }
                            </Button>
                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: 'text.secondary' }}>
                                Prin autentificare ești de acord cu{' '}
                                <a href="#/privacy" style={{ color: '#1976d2' }}>Politica de confidențialitate</a>.
                            </Typography>
                        </form>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};
